import React, { useMemo } from 'react';
import { Calendar, momentLocalizer, SlotInfo, EventProps, Components } from 'react-big-calendar'
import moment from 'moment-timezone'

import { BlackoutFragment, BookingFragment } from 'src/state/gql/_types';
import { getContrastColor } from 'src/utils/calendar';
import { formatTime } from 'src/utils/dates';
import { Box, Stack } from '@mantine/core';
import { CalendarToolBar } from 'src/components/calendar/CalendarToolBar';
import { useCalendar } from 'src/components/calendar/CalendarContext';

moment.updateLocale('en', {
    week: { dow: 1 }
})

const localizer = momentLocalizer(moment)

interface BookingsCalendarProps {
    bookings: BookingFragment[];
    blackouts: BlackoutFragment[];
    onEventClick?: (booking: BookingFragment) => void;
}

const mapBookingToEvent = (booking: BookingFragment) => {

    const tz = booking.venue?.timezone || 'America/New_York';

    const startDateTime = moment.tz(`${booking.date}T${booking.set_start_time || booking.event_start_time}`, tz).toDate();
    const endDateTime = moment.tz(`${booking.date}T${booking.set_end_time || booking.event_end_time}`, tz).toDate();

    let eventColor = booking.talent && booking.talent.color ? booking.talent.color : '#CCCCCC'
    let eventTextColor = getContrastColor(eventColor)

    let assignedTalent = booking.talent_id && booking.talent ? booking.talent.alias ? booking.talent?.alias : booking.talent?.name : 'Unassigned'
    let assignedVenue = booking.venue_id && booking.venue ? `@ ${booking.venue.name}` : ''

    return {
        id: booking.id,
        title: `${assignedTalent} ${assignedVenue}`,
        start: startDateTime,
        end: endDateTime,
        resource: booking,
        eventColor,
        eventTextColor,
        type: 'booking'
    };
};

export type CalendarEvent = {
    type: 'booking' | 'blackout';
    resource: BookingFragment & BlackoutFragment;
    title: string;
}

const mapBlackoutToBackgroundEvent = (blackout: BlackoutFragment) => {

    const tz = blackout.talent?.timezone || 'America/New_York';

    const startDateTime = moment.tz(blackout.start_at, tz).toDate();
    const endDateTime = moment.tz(blackout.end_at, tz).toDate();

    let eventColor = blackout.talent && blackout.talent.color ? blackout.talent.color : '#CCCCCC'
    let eventTextColor = getContrastColor(eventColor)

    let assignedTalent = blackout.talent_id && blackout.talent ? blackout.talent.alias ? blackout.talent?.alias : blackout.talent?.name : 'Unassigned'

    return {
        id: blackout.id,
        title: `${assignedTalent} ${blackout.description}`,
        start: startDateTime,
        end: endDateTime,
        resource: blackout,
        eventColor,
        eventTextColor,
        type: 'blackout'
    };

};

function CalendarEvent({ event }: EventProps<CalendarEvent>) {
    const timeLabel = event.resource.set_start_time || event.resource.event_start_time;
    if (event.type === 'booking') {
        return (
            <div>
                <div style={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-start' }}>
                    <span style={{ fontWeight: 600 }}>{event.type === 'booking' && formatTime(timeLabel)} - </span>
                    <span>{event.title}</span>
                    <span style={{ marginLeft: 'auto', opacity: 0.8 }}>[{event.type === 'booking' && 'event_start_time' in event.resource && event.resource.status}]</span>
                </div>
            </div>
        );
    }
    if (event.type === 'blackout') {
        return (
            <div>
                <div style={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-start' }}>
                    <span>{event.title}</span>
                </div>
            </div>
        );
    }
}

function CalendarEventDetailed({ event }: EventProps<CalendarEvent>) {
    if (event.type === 'booking') {
        return (
            <Stack gap={4} style={{ lineHeight: '.7rem' }}>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'flex-start' }}>
                    <span style={{ fontWeight: 600 }}>{event.title}</span>
                    <span style={{ marginLeft: 'auto', opacity: 0.8 }}>[{event.resource.status}]</span>
                </div>
                <div style={{}}>Venue: {event.resource.venue?.name}</div>
                <div style={{}}>Area: {event.resource.area?.name}</div>
            </Stack>
        );
    }
    if (event.type === 'blackout') {
        return (
            <Stack gap={4} style={{ lineHeight: '.7rem' }}>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'flex-start' }}>
                    <span style={{ fontWeight: 600 }}>{event.title}</span>
                </div>
            </Stack>
        );
    }
}

const BookingsCalendar: React.FC<BookingsCalendarProps> = ({
    bookings,
    blackouts,
    onEventClick
}) => {

    const { view, setView, date, setDate, setDateRange } = useCalendar();

    const events = useMemo(() => {
        return bookings.map(mapBookingToEvent);
    }, [bookings]);

    const backgroundEvents = useMemo(() => {
        return blackouts.map(mapBlackoutToBackgroundEvent);
    }, [bookings]);

    const components = useMemo<Components<CalendarEvent>>(() => ({
        toolbar: CalendarToolBar,
        month: { event: CalendarEvent },
        week: { event: CalendarEventDetailed },
        day: { event: CalendarEventDetailed },
        agenda: { event: CalendarEventDetailed }
    }), []);

    const handleSelectSlot = (slotInfo: SlotInfo) => {
        setView('day');
        setDate(slotInfo.start);
    };

    const handleRangeChange = (range: Date[] | { start: Date; end: Date }) => {
        let start: Date;
        let end: Date;
        if (Array.isArray(range)) {
            start = range[0];
            end = range[range.length - 1];
        } else {
            start = range.start;
            end = range.end;
        }
        setDateRange({ start, end });
    };

    const handleSelectEvent = (event: any) => {
        if (event.type === 'booking') {
            const booking = event.resource as BookingFragment;
            if (onEventClick && booking) {
                onEventClick(booking);
            }
        }
    };

    return (
        <Box h="calc(100vh - 200px)" >
            <Calendar
                localizer={localizer}
                events={[...events, ...backgroundEvents]}
                startAccessor="start"
                endAccessor="end"
                style={{ height: '100%' }}
                view={view}
                date={date}
                onView={setView}
                onNavigate={setDate}
                onSelectSlot={handleSelectSlot}
                onSelectEvent={handleSelectEvent}
                onRangeChange={handleRangeChange}
                selectable
                eventPropGetter={(event) => {
                    if (event.type === 'blackout') {
                        return {
                            style: {
                                borderColor: event.eventColor,
                                backgroundColor: '#FFFFFF66',
                                color: '#444444',
                                borderWidth: 1,
                                borderStyle: 'dashed'
                            }
                        }
                    }
                    return {
                        style: {
                            borderColor: event.eventColor,
                            backgroundColor: event.eventColor,
                            color: event.eventTextColor
                        }
                    }

                }}
                components={components}
            />
        </Box>
    );
};

export default BookingsCalendar;